import { WIDGET_TYPES } from 'embed_v2/types/version'
import { bgPositionToValue, widthToValue } from 'core/helpers/styles'
import cssReset from './css_reset'
import type Version from 'embed_v2/types/version'

const extractStepLayoutStyles = (options: Version['options']['layout']) => ({
  position: 'relative',
  display: 'inline-block',
  margin: '0 auto',
  height: '100%',
  width: '100%',
  backgroundRepeat: 'no-repeat',
  backgroundColor: `rgba(${options.bg_color})`,
  backgroundImage: options.bg_image_url ? `url(${options.bg_image_url})` : '',
  backgroundPosition:
    options.bg_image_position === 'advanced'
      ? options.bg_image_position_advanced
      : bgPositionToValue(options.bg_image_position),
  backgroundSize: options.bg_image_size === 'advanced' ? options.bg_image_size_advanced : options.bg_image_size,
  marginLeft: options.align === 'left' ? '0' : 'auto',
  marginRight: options.align === 'right' ? '0' : 'auto',
  marginTop: `${options.margin_top}px`,
  paddingTop: `${options.padding_top}px`,
  paddingRight: `${options.padding_right}px`,
  paddingBottom: `${options.padding_bottom}px`,
  paddingLeft: `${options.padding_left}px`,
  borderTop: options.border_side?.[0]
    ? `${options.border_size}px ${options.border_style} rgba(${options.border_color})`
    : 'none',
  borderRight: options.border_side?.[1]
    ? `${options.border_size}px ${options.border_style} rgba(${options.border_color})`
    : 'none',
  borderBottom: options.border_side?.[2]
    ? `${options.border_size}px ${options.border_style} rgba(${options.border_color})`
    : 'none',
  borderLeft: options.border_side?.[3]
    ? `${options.border_size}px ${options.border_style} rgba(${options.border_color})`
    : 'none',
  borderTopLeftRadius: `${options.border_top_left}px`,
  borderTopRightRadius: `${options.border_top_right}px`,
  borderBottomRightRadius: `${options.border_bottom_right}px`,
  borderBottomLeftRadius: `${options.border_bottom_left}px`,
  boxShadow: options.box_shadow
})

const extractStepHandlerStyles = (options: Version['options']['handler']) => ({
  position: 'relative',
  display: 'inline-block',
  width: '60px',
  height: '60px',
  backgroundColor: `rgba(${options.bg_color})`,
  borderTop: options.border_side[0]
    ? `${options.border_size}px ${options.border_style} rgba(${options.border_color})`
    : 'none',
  borderRight: options.border_side[1]
    ? `${options.border_size}px ${options.border_style} rgba(${options.border_color})`
    : 'none',
  borderBottom: options.border_side[2]
    ? `${options.border_size}px ${options.border_style} rgba(${options.border_color})`
    : 'none',
  borderLeft: options.border_side[3]
    ? `${options.border_size}px ${options.border_style} rgba(${options.border_color})`
    : 'none',
  borderTopLeftRadius: `${options.border_top_left}px`,
  borderTopRightRadius: `${options.border_top_right}px`,
  borderBottomRightRadius: `${options.border_bottom_right}px`,
  borderBottomLeftRadius: `${options.border_bottom_left}px`,
  boxSizing: 'border-box'
})

const propToStyle = (prop: string) => {
  if (prop === 'bgColor') return 'background-color'
  if (prop === 'textColor') return 'color'

  return prop.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`)
}

class StyleSheetManager {
  id: string
  version: Version

  constructor(id: string, version: Version) {
    this.id = id
    this.version = version
  }

  inject() {
    this.injectResecCss()
    this.injectMainCss()
  }

  injectMainCss() {
    const styleId = `wf_styles_main_${this.id}`
    const styleElement = document.getElementById(styleId)

    if (styleElement) return

    window.logDifference('Widget useEffect 3', this.id)

    const getStylePrefix = (type: string, id?: string) => {
      let result = `#wf_${this.id} .wf_container .wf_${type}`

      if (id) {
        result += `_${id}`
      }

      return result
    }

    let css = ''

    const containerAlign = { left: 'flex-start', center: 'center', right: 'flex-end' }[
      this.version.options.layout.align
    ]

    const getLayoutWidth = () => {
      const { width, width_fixed, width_percent } = this.version.options.layout

      return widthToValue(width, width_fixed, width_percent)
    }

    css += `
      .wf_target #wf_${this.id} .wf_container {display: flex;flex-direction: column;align-items: ${containerAlign};justify-content: center;}
      #wf_${this.id} .wf_container .wf_box {width: ${getLayoutWidth()}; max-width: 100%;}
      #wf_${this.id} {line-height:1.3;}
      #wf_${this.id} .wf_layout {overflow: auto;}
      #wf_${this.id} .wf_layout .wf_layout_error {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 10px;
        padding: 8px 20px;
        background: rgba(255, 0, 0, 0.75);
        color: #ffffff;font-size: 13px;
        border-radius: 8px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
        background: #ff0033;
        z-index: 100;
        text-align: center;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, sans-serif;
      }
      #wf_${this.id} .wf_layout .wf_layout_error .wf_layout_error_close {
        position: absolute;
        top: 0px;
        right: 4px;
        font-size: 20px;
        cursor: pointer;
      }

      #wf_${this.id} .wf_step {visibility: hidden;}
      #wf_${this.id} .wf_step.wf_active {visibility: visible;}

      #wf_${this.id} .wf_datetime .wf_datetime_radio {display: flex;align-items: center;flex-direction: column; box-sizing: border-box;height: 50px;padding: 5px 15px;cursor: pointer;position: relative;}
      #wf_${this.id} .wf_datetime .wf_datetime_radio:not(:first-child) {margin-top: 10px;}
      #wf_${this.id} .wf_datetime .wf_datetime_icon_wrapper {position: absolute;top: 15px;left: 10px;width: 17px;height: 17px;border-radius: 8.5px;}
      #wf_${this.id} .wf_datetime .wf_datetime_icon {display: flex;align-items: center;justify-content: center;width: 100%;height: 100%;}

      #wf_${this.id} .wf_element .wf_field:hover .wf_error_tooltip {opacity: 1;transition-delay: 0s;}
      #wf_${this.id} .wf_field .wf_error_tooltip {line-height: 16px;font-size: 11px;color: #FF8888;position: absolute;top: 3px;right: 5px;opacity: 0;transition: opacity 120ms ease-in-out;transition-delay: 2s;user-select: none;}

      #wf_${this.id} input.wf_input { margin: inherit; }
      #wf_${this.id} .wf_field .wf_input.wf_error { border: 1px solid red !important; }
      #wf_${this.id} .wf_field .wf_textarea.wf_error { border: 1px solid red !important; }
      #wf_${this.id} .wf_field .wf_icon_wrapper.wf_error { border: 1px solid red !important; }
      #wf_${this.id} .wf_field .wf_dropdown_control.wf_error { border: 1px solid red !important; }

      #wf_${this.id} .wf_row {flex-wrap: wrap;}

      #wf_${this.id} .wf_element .wf_video_embed {position: relative;}
      #wf_${this.id} .wf_element .wf_video_embed .wf_video_padding {padding: 0 0 56.25%;}
      #wf_${this.id} .wf_element .wf_video_embed .wf_video {position: absolute;top: 0;left: 0;width: 100%;height: 100%;background: #000;}
      #wf_${this.id} .wf_element .wf_video_embed .wf_video iframe {position: absolute;top: 0;left: 0;width: 100%!important;height: 100%!important;padding: 0;margin: 0;max-width: 100%;max-height: 100%;}

      #wf_${this.id} .wf_element button.wf_button[disabled] {box-shadow: inset 0 0 0 10em rgba(255, 255, 255, 0.3) !important;}
    `

    switch (this.version.widget_type) {
      case WIDGET_TYPES.FLOATER:
        css += `
          #wf_${this.id} .wf_container .wf_box.wf_floater_box {max-width: calc(100% - 60px);}
          #wf_${this.id} .wf_floater_wrapper .wf_handler {cursor:pointer;}
          #wf_${this.id} .wf_floater_wrapper .wf_handler .wf_handler_icon {display: flex;align-items: center;justify-content: center;position: absolute;top: 0px;bottom: 0px;width: 100%;transition: transform 150ms ease-in-out, opacity 90ms ease-in-out;}
          #wf_${this.id} .wf_floater_wrapper .wf_handler .wf_handler_icon.wf_handler_logo {opacity:1;transform:rotateZ(0);}
          #wf_${this.id} .wf_floater_wrapper .wf_handler .wf_handler_icon.wf_handler_close {opacity:0;transform:rotateZ(0);color: rgba(${this.version.options.handler.close_cross_color});}
          #wf_${this.id} .wf_floater_wrapper .wf_handler .wf_handler_icon.wf_handler_logo.wf_open {opacity:0;transform:rotateZ(90deg);}
          #wf_${this.id} .wf_floater_wrapper .wf_handler .wf_handler_icon.wf_handler_close.wf_open {opacity:1;transform:rotateZ(90deg);}
          #wf_${this.id} .wf_floater_wrapper {display: flex;flex-direction: column;}
          #wf_${this.id} .wf_floater_wrapper .wf_floater_box {z-index: 2147483001;position: fixed;display: flex;flex-direction: column;margin-bottom: 0;max-height: calc(100% - 115px);bottom: 100px;right: -99999px;box-sizing: border-box;transition: opacity 150ms;}
          #wf_${this.id} .wf_floater_wrapper .wf_floater_handler {z-index: 2147483001;border-radius: 50%;position: fixed;display: flex;right: 20px;bottom: 20px;box-shadow: 0 3px 6px 0 rgb(0 0 0 / 16%);transition: transform 90ms;transform: scale(1);}
          #wf_${this.id} .wf_floater_wrapper .wf_floater_handler:hover {transform: scale(1.02);}
          #wf_${this.id} .floater {width: 350px;height: 615px;}
          #wf_${this.id} #webby-logo {fill: rgba(${this.version.options.handler.logo_color || '255,255,255,1'});}
          #wf_${this.id} .wf_floater_wrapper .wf_handler .wf_handler_icon.wf_handler_logo img {width: 40px;height: 40px;}
        `
        break
      case WIDGET_TYPES.MODAL:
        css += `
          #wf_${this.id} .wf_container .wf_box.wf_modal_box {max-width: calc(100% - 26px);}
          #wf_${this.id} .wf_container .wf_modal_box .wf_modal_close svg .wf_close_icon_container {fill: rgba(${this.version.options.close_button.bg_color});}
          #wf_${this.id} .wf_container .wf_modal_box .wf_modal_close svg {color: rgba(${this.version.options.close_button.cross_color});}
        `
        break
      case WIDGET_TYPES.THANK_YOU:
        css += `
          #wf_${this.id} .wf_thankyou_wrapper, #wf_${this.id} .wf_thankyou_wrapper .wf_thankyou_box {display: flex;flex-direction: column;}
        `
        break
      case WIDGET_TYPES.REGISTRATION_PAGE:
        css += `
          #wf_${this.id} {width: 100%; height: 100%;}
          #wf_${this.id} .wf_registrationpage_wrapper { height: 100%; }
          #wf_${this.id} .wf_registrationpage_wrapper .wf_layout.wf_registration_page { height: 100%; margin: 0 auto; display: block; }
          #wf_${this.id} .wf_registrationpage_wrapper .wf_registrationpage_box { height: 100%; }
        `

        break
      default:
        break
    }

    let stepStyles = ''
    let stepHandlerStyles = ''
    for (const [key, value] of Object.entries(extractStepLayoutStyles(this.version.options.layout))) {
      stepStyles += `${propToStyle(key)}:${value};`
    }
    css += `${getStylePrefix('layout')} {${stepStyles}}\n`

    if (this.version.widget_type === WIDGET_TYPES.FLOATER) {
      for (const [key, value] of Object.entries(extractStepHandlerStyles(this.version.options.handler))) {
        stepHandlerStyles += `${propToStyle(key)}:${value};`
      }
      css += `${getStylePrefix('handler')} {${stepHandlerStyles}}\n`
    }

    const style = document.createElement('style')

    document.head.appendChild(style)

    style.id = styleId
    style.appendChild(document.createTextNode(css))

    window.logDifference('Widget useEffect 3 end', this.id)
  }

  injectResecCss() {
    const styleId = `wf_styles_reset_${this.id}`
    const styleElement = document.getElementById(styleId)

    if (styleElement) return

    window.logDifference('Widget useEffect 2', this.id)
    const css = cssReset(this.id)
    const style = document.createElement('style')

    document.head.appendChild(style)

    style.id = styleId
    style.appendChild(document.createTextNode(css))
    window.logDifference('Widget useEffect 2 end', this.id)
  }
}

export default StyleSheetManager
